import React, { useState } from "react";
import styles from "./sidebar.module.css";
import {
  faArrowRightFromBracket,
  faBuilding,
  faChevronRight,
  faCog,
  faGlobe,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "../../contexts/SidebarContext";
import { useTranslation } from 'react-i18next';

function Logout() {
  const { t } = useTranslation();
  const logout = () => {
    window.location.replace("/login");
    localStorage.clear();
  };

  return (
    <div className={styles.logoutContainer} onClick={() => logout()}>
      <div className={styles.logout}>
        <div className={styles.logoutTip} />
        <div className={"d-flex align-items-center"} style={{ zIndex: 100 }}>
          <FontAwesomeIcon className={"pe-2"} icon={faArrowRightFromBracket} />
          <div
            style={{ fontSize: "11px", letterSpacing: "8%", fontWeight: 350 }}
          >
            {t('sidebar.logout')}
          </div>
        </div>
      </div>
    </div>
  );
}

function SidebarItem({ item, selected, onClick }) {
  const navigate = useNavigate();
  const { close, isMobile } = useSidebar();

  return (
    <div
      className={`${styles.sidebarItem} ${selected ? styles.sidebarItemSelected : ""}`}
      onClick={() => {
        navigate(item.route);
        if (isMobile) close();
      }}
    >
      {selected && <div className={styles.sidebarItemPill} />}
      {item.faIcon ? (
        <FontAwesomeIcon icon={item.faIcon} className={"me-3"} style={{ width: "19px", fontSize: "15px" }} />
      ) : (
        <img alt={item.name} src={item.icon} className={"me-3"} width={"19px"} />
      )}
      <div>{item.name}</div>
    </div>
  );
}

function Profile() {
  const [showLogout, setShowLogout] = useState(false);
  const first_name = localStorage.getItem("first_name");
  const last_name = localStorage.getItem("last_name");
  const image = localStorage.getItem("image");
  const name = `${first_name} ${last_name}`;
  const ref = React.useRef(null);

  React.useEffect(() => {
    if (!showLogout) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShowLogout(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showLogout]);

  return (
    <div
      ref={ref}
      className={styles.profile}
      onClick={() => setShowLogout((prev) => !prev)}
    >
      {image ? (
        <img src={image} alt="Profile" className={styles.profileImage} />
      ) : (
        <div className={styles.profileImage} />
      )}
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div style={{ fontWeight: "550", fontSize: "11px" }}>{name}</div>
      </div>
      {showLogout && <Logout />}
    </div>
  );
}

function LanguageSelector() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.substring(0, 2) || 'es';
  const languages = [
    { code: 'es', label: 'ES' },
    { code: 'en', label: 'EN' },
    { code: 'fr', label: 'FR' },
  ];

  const handleChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('pdui_language', lang);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px 0' }}>
      <FontAwesomeIcon icon={faGlobe} style={{ color: '#9ca3af', fontSize: '12px', marginRight: '4px' }} />
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => handleChange(lang.code)}
          style={{
            background: currentLang === lang.code ? '#3b82f6' : 'transparent',
            color: currentLang === lang.code ? '#fff' : '#9ca3af',
            border: 'none',
            borderRadius: '4px',
            padding: '2px 8px',
            fontSize: '11px',
            fontWeight: currentLang === lang.code ? 600 : 400,
            cursor: 'pointer',
          }}
        >
          {lang.label}
        </button>
      ))}
    </div>
  );
}

export default function Sidebar({ selectedIndex }) {
  const { t } = useTranslation();
  const { isOpen, isMobile, close } = useSidebar();
  const { pathname } = useLocation();
  const hasImu = localStorage.getItem("has_imu") === "true";

  // El ítem activo se decide por la ruta, no por un índice: con el mapa IMU
  // opcional los índices cambiaban según la empresa y cada pantalla tenía
  // que calcularlos a mano. `selectedIndex` se sigue aceptando por
  // compatibilidad pero ya no hace falta.
  const items = [
    { name: t('sidebar.dashboard'), icon: "/sidebar/dashboard.png", route: "/", match: (p) => p === "/" },
    { name: t('sidebar.pduis'), icon: "/sidebar/pduis.png", route: "/list", match: (p) => p.startsWith("/list") || p.startsWith("/detail") },
    ...(hasImu
      ? [{ name: t('sidebar.imuHeatmap'), icon: "/sidebar/quality.png", route: "/imu", match: (p) => p.startsWith("/imu") }]
      : []),
    { name: t('sidebar.networkMap'), icon: "/sidebar/net.png", route: "/network-map", match: (p) => p.startsWith("/network-map") },
    { name: t('sidebar.company'), faIcon: faBuilding, route: "/company", match: (p) => p.startsWith("/company") },
  ];

  return (
    <>
      {isMobile && (
        <div
          className={`${styles.overlay} ${isOpen ? styles.overlayVisible : ""}`}
          onClick={close}
        />
      )}

      <div
        className={`${styles.container} ${isMobile && isOpen ? styles.containerOpen : ""}`}
      >
        {/* Close button for mobile */}
        <button className={styles.closeButton} onClick={close} aria-label={t('sidebar.closeMenu')}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className={`${styles.logoContainer}`}>
          <img
            alt={"pritech"}
            src={"/branding/PRITECH_BLANCO_COLOR.png"}
            height={"38px"}
          />
        </div>
        <div className={"d-flex justify-content-center w-100"}>
          <div
            className={styles.sidebarDivider}
            style={{ marginBottom: "23px" }}
          />
        </div>
        <div className={styles.menu}>
          {items?.map((item, index) => {
            return (
              <SidebarItem
                selected={item.match(pathname)}
                key={index}
                item={item}
              />
            );
          })}
        </div>
        <div className={styles.sidebarBottom}>
          <div className={styles.sidebarDivider} />
          <LanguageSelector />
          <Profile />
        </div>
      </div>
    </>
  );
}
